import { Checkbox, Divider, FormControlLabel } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { TreeItem, TreeView } from '@material-ui/lab';
import { cloneDeep } from 'lodash-es';
import React, { useState } from 'react';

import { ComscoreContextualTaxonomy } from '~/src/dash/components/comscore/contextual/taxonomy';
import {
  IComscoreContextualData,
  IComscoreContextualNode,
  parseComscoreContextualData,
  parseComscoreContextualTree,
} from '~/src/dash/components/comscore/contextual/utils';

function getCheckboxStatus(selectedIDs: string[], id: string | undefined): boolean {
  if (id === undefined || id === '' || selectedIDs.length === 0) {
    return false;
  }
  return selectedIDs.indexOf(id) > -1;
}

function handleToggleID(
  selectedIDs: string[],
  id: string | undefined,
  onToggleID: (updatedSelectedIDs: string[]) => void,
) {
  const updatedSelectedIDs = cloneDeep(selectedIDs);
  if (id === undefined || id === '') {
    return;
  }
  if (selectedIDs.indexOf(id) === -1) {
    updatedSelectedIDs.push(id);
  } else {
    const idx = updatedSelectedIDs.indexOf(id);
    if (idx > -1) {
      updatedSelectedIDs.splice(idx, 1);
    }
  }

  onToggleID(updatedSelectedIDs);
}

interface IComscoreContextualTreeProps {
  classes: any;
  selectedIDs: string[];
  comscoreContextualTree: IComscoreContextualNode[];
  onToggleID: (selectedID: string[]) => void;
}

function ComscoreContextualTree({
  classes,
  selectedIDs,
  comscoreContextualTree,
  onToggleID,
}: IComscoreContextualTreeProps) {
  let id = 0;

  const renderComscoreContextualNode = (node: IComscoreContextualNode) => {
    id += 1;
    const nodeID = (node.data || {}).ID;
    const nodeDescription = (node.data || {}).Description;
    if (node.children.length === 0) {
      return (
        <TreeItem nodeId={'' + id} label={node.value}>
          <FormControlLabel
            control={
              <Checkbox
                checked={getCheckboxStatus(selectedIDs, nodeID)}
                name={nodeID}
                onChange={() => {
                  handleToggleID(selectedIDs, nodeID, onToggleID);
                }}
                color='primary'
              />
            }
            label={`${nodeID} | ${nodeDescription}`}
          />
        </TreeItem>
      );
    } else {
      return (
        <TreeItem nodeId={'' + id} label={node.value}>
          {node.children.map((child: IComscoreContextualNode) => {
            return renderComscoreContextualNode(child);
          })}
        </TreeItem>
      );
    }
  };

  return (
    <div>
      <div className={classes.section}>
        <TreeView
          className={classes.root}
          defaultCollapseIcon={<ExpandMoreIcon />}
          defaultExpandIcon={<ChevronRightIcon />}
        >
          {comscoreContextualTree.map((node: IComscoreContextualNode) => {
            return renderComscoreContextualNode(node);
          })}
        </TreeView>
      </div>
    </div>
  );
}

interface IComscoreContextualListProps {
  classes: any;
  selectedIDs: string[];
  comscoreContextualData: IComscoreContextualData[];
  onToggleID: (selectedID: string[]) => void;
}
function ComscoreContextualList({
  classes,
  selectedIDs,
  comscoreContextualData,
  onToggleID,
}: IComscoreContextualListProps) {
  return (
    <>
      <div className={classes.section}>
        {comscoreContextualData.map((data: IComscoreContextualData) => {
          return (
            <FormControlLabel
              control={
                <Checkbox
                  checked={getCheckboxStatus(selectedIDs, data.ID)}
                  name={data.ID}
                  onChange={() => {
                    handleToggleID(selectedIDs, data.ID, onToggleID);
                  }}
                  color='primary'
                />
              }
              label={`${data.ID} | ${data.Description}`}
            />
          );
        })}
      </div>
    </>
  );
}

interface IComscoreContextualSelectorProps {
  classes: any;
  selectedIDs: string[];
  onToggleID: (selectedID: string[]) => void;
}

export function ComscoreContextualSelector({
  classes,
  selectedIDs,
  onToggleID,
}: IComscoreContextualSelectorProps) {
  const comscoreContextualData = parseComscoreContextualData(ComscoreContextualTaxonomy);
  const comscoreContextualTree = parseComscoreContextualTree(comscoreContextualData);

  const [showComscoreContextualTree, setShowComscoreContextualTree] = useState(
    selectedIDs.length === 0,
  );
  return (
    <div>
      <div>
        <Checkbox
          name='showComscoreContextualTree'
          checked={showComscoreContextualTree}
          onChange={() => setShowComscoreContextualTree(!showComscoreContextualTree)}
        />
        Display Comscore Contextual Selection as Tree
      </div>
      <Divider
        style={{
          marginBottom: '24px',
          marginTop: '14px',
          height: '2px',
          maxWidth: '480px',
        }}
      />
      {showComscoreContextualTree
        ? ComscoreContextualTree({
            classes,
            selectedIDs,
            comscoreContextualTree,
            onToggleID,
          })
        : ComscoreContextualList({
            classes,
            selectedIDs,
            comscoreContextualData: comscoreContextualData.filter(
              (data: IComscoreContextualData) => {
                return selectedIDs.includes(data.ID);
              },
            ),
            onToggleID,
          })}
    </div>
  );
}
